import { Action } from 'redux';
import { HttpStatusCode } from '@sonar-software/sonar-http';
import { isFunction } from '@sonar-software/sonar-ts-lib';
import { useDispatch } from 'react-redux';

import { AsyncStateSelector, asyncStateStatusCodeSelector, asyncStateStatusSelector } from '@lib/selectors';
import { AsyncStateStatus, isIdle, isRequested } from '@sonar-software/xuder';
import { ProcessingResult, usePrevious, useProcessing, UseProcessingResult, useSonarSelector } from '@hooks';
import { useCallback, useEffect, useRef, useState } from 'react';

export type StatusCodeHandlers<T> = Partial<Record<HttpStatusCode, ((status: HttpStatusCode) => void) | T>>;
export type StatusHandlers<T> = Partial<Record<AsyncStateStatus, ((status: AsyncStateStatus) => void) | T>>;

export type UseAsyncProcessingOptions<T> = {
	allowMultipleHandlers?: boolean;
	statusCodeHandlers?: StatusCodeHandlers<T>;
	statusHandlers?: StatusHandlers<T>;
	onStatus?: (status: AsyncStateStatus, options: T) => void;
	onStatusCode?: (status: HttpStatusCode, options: T) => void;
	onReset?: () => void;
	resetAction?: () => Action;
	stateSelector: AsyncStateSelector<unknown>;
	watchAllCalls?: boolean;
};

export type UseAsyncProcessingResult = UseProcessingResult & {
	watchNextCall: () => void;
};

const defaultStatusHandlers: StatusHandlers<ProcessingResult> = {
	errored: { success: false },
	failed: { success: false },
	received: { success: true }
};

export function useAsyncProcessing<T>(hookOptions: UseAsyncProcessingOptions<T>): UseAsyncProcessingResult {
	const process = useProcessing();
	const storedOptions = useRef(hookOptions);

	storedOptions.current = hookOptions;

	const { resetAction, onReset, watchAllCalls } = storedOptions.current;

	const [ watchingState, setWatchingState ] = useState(watchAllCalls);
	const status = useSonarSelector(asyncStateStatusSelector(hookOptions.stateSelector));
	const statusCode = useSonarSelector(asyncStateStatusCodeSelector(hookOptions.stateSelector));
	const previousStatus = usePrevious(status, status);
	const statusChanged = status !== previousStatus;
	const dispatch = useDispatch();

	const watchNextCall = useCallback(() => {
		setWatchingState(true);
	}, []);

	const reset = useCallback(() => {
		if (resetAction) {
			dispatch(resetAction());
		}

		if (onReset) {
			onReset();
		}

		process.reset();
	}, [ dispatch, onReset, process, resetAction ]);

	useEffect(() => {
		if (!statusChanged) {
			return;
		}

		if (isIdle(status)) {
			setWatchingState(watchAllCalls);
		}

		if (watchingState && isRequested(status)) {
			process.start();
		}

		if (!watchingState || isIdle(status) || isRequested(status)) {
			return;
		}

		let handled = false;

		const {
			allowMultipleHandlers,
			statusHandlers = defaultStatusHandlers,
			statusCodeHandlers,
			onStatus,
			onStatusCode
		} = storedOptions.current;

		if (statusCode && statusCodeHandlers) {
			const handler = statusCodeHandlers[ statusCode ];

			if (isFunction(handler)) {
				handler(statusCode);
			} else if (onStatusCode && handler) {
				onStatusCode(statusCode, handler);
			} else if (handler) {
				process.stop(handler as unknown as ProcessingResult);
			}

			if (!allowMultipleHandlers && handler) {
				handled = true;
			}
		}

		if (!handled && status) {
			const handler = statusHandlers?.[ status ];

			if (isFunction(handler)) {
				handler(status);
			} else if (onStatus && handler) {
				onStatus(status, handler as T);
			} else if (handler) {
				process.stop(handler as ProcessingResult);
			}
		}

		reset();
	}, [ statusChanged, process, reset, status, statusCode, watchAllCalls, watchingState ]);

	return {
		...process,
		watchNextCall
	};
}
